/**
 * FluentUI Design Kit Theme Foundation
 */

import { createLightTheme, createDarkTheme, BrandVariants } from '@fluentui/react-components';
import type { CustomTheme, ThemeMode } from './types';
import { tokens, type FluentUIDesignKitTokens } from './tokens';

function createBrandVariantsFromDesignKit(designKitTokens: FluentUIDesignKitTokens, mode: ThemeMode): BrandVariants {
  const tokens = mode === 'light' ? designKitTokens.light : designKitTokens.dark;

  // In dark mode, colorBrand70 (#389120) is too light for white text (4:1, below 4.5:1 WCAG AA).
  // Use colorBrandShade10 (#21750B) for hover slots instead — gives 5.8:1 contrast with white.
  const hoverColor = mode === 'dark' ? tokens.colorBrandShade10 : tokens.colorBrand70;

  return {
    10: tokens.colorBrand60, // Lightest - for very subtle backgrounds
    20: tokens.colorBrand60,
    30: tokens.colorBrand60,
    40: tokens.colorBrand60,
    50: hoverColor, // Hover states and secondary elements
    60: hoverColor,
    70: hoverColor,
    80: tokens.colorBrand80, // Primary brand color - main interactive elements
    90: tokens.colorBrand90, // Dark - for pressed states and emphasis
    100: tokens.colorBrand100, // Darkest - for maximum contrast
    110: tokens.colorBrand100,
    120: tokens.colorBrand100,
    130: tokens.colorBrand100,
    140: tokens.colorBrand100,
    150: tokens.colorBrand100,
    160: tokens.colorBrand100,
  };
}

export function createCustomTheme(mode: ThemeMode, designKitTokens: FluentUIDesignKitTokens = tokens): CustomTheme {
  const brandVariants = createBrandVariantsFromDesignKit(designKitTokens, mode);

  const baseTheme = mode === 'light' ? createLightTheme(brandVariants) : createDarkTheme(brandVariants);

  const customTheme: CustomTheme = {
    ...baseTheme,
    fontFamilyBase: designKitTokens.fontFamilyBase,
    fontFamilyMonospace: designKitTokens.fontFamilyBase,
    fontFamilyNumeric: designKitTokens.fontFamilyBase,
    designKitTokens,
    currentMode: mode,
  };

  return customTheme;
}

export function getCurrentModeTokens(theme: CustomTheme) {
  return theme.designKitTokens[theme.currentMode];
}
